import React, { useState } from 'react';
import { Download, FileText, Loader2 } from 'lucide-react';
import { generateProfessionalPDF, generateFileName, PDFExtractionOptions } from '../../utils/professionalPDFGeneratorUltimate';

interface ProfessionalPDFExtractionButtonProps {
  type: 'company' | 'financial-institution';
  formData: any;
  entityName: string;
  disabled?: boolean;
}

const ProfessionalPDFExtractionButton: React.FC<ProfessionalPDFExtractionButtonProps> = ({
  type,
  formData,
  entityName,
  disabled = false
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const handleExtraction = async (isBlank: boolean = false) => {
    if (disabled || isLoading) return;

    setIsLoading(true);
    try {
      const filename = generateFileName(type, entityName, isBlank ? 'blank' : 'complete');
      
      const options: PDFExtractionOptions = {
        filename,
        type,
        title: type === 'company' ? 'Formulaire Entreprise' : 'Formulaire Institution Financière',
        subtitle: isBlank ? 'Version vierge' : 'Version complétée'
      };

      generateProfessionalPDF(formData, isBlank, options);
      
      setShowModal(false);
    } catch (error) {
      console.error('Erreur lors de l\'extraction PDF:', error);
      alert('Erreur lors de la génération du PDF. Veuillez réessayer.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setShowModal(true)}
        disabled={disabled || isLoading}
        className={`
          inline-flex items-center px-4 py-2 rounded-md text-sm font-medium
          ${disabled || isLoading
            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
            : 'bg-[#197ca8] text-white hover:bg-[#156992] active:bg-[#134a73]'
          }
          transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-[#197ca8] focus:ring-offset-2
        `}
      >
        {isLoading ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            Génération...
          </>
        ) : (
          <>
            <Download className="w-4 h-4 mr-2" />
            Extraire PDF
          </>
        )}
      </button>

      {/* Modal de sélection */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Choisir le type d'extraction
            </h3>
            
            <p className="text-sm text-gray-600 mb-6">
              Voulez-vous extraire le formulaire avec vos données actuelles ou un formulaire vierge ?
            </p>

            <div className="space-y-3">
              {/* Option formulaire complété */}
              <button
                onClick={() => handleExtraction(false)}
                disabled={isLoading}
                className={`
                  w-full flex items-center justify-between p-4 border-2 rounded-lg transition-all
                  ${isLoading 
                    ? 'bg-gray-100 border-gray-200 cursor-not-allowed' 
                    : 'bg-[#197ca8] border-[#197ca8] text-white hover:bg-[#156992]'
                  }
                `}
              >
                <div className="flex items-center">
                  <FileText className="w-5 h-5 mr-3" />
                  <div className="text-left">
                    <div className="font-medium">Formulaire complété</div>
                    <div className="text-xs opacity-70">Avec vos données actuelles</div>
                  </div>
                </div>
                {isLoading && <Loader2 className="w-4 h-4 animate-spin" />}
              </button>

              {/* Option formulaire vierge */}
              <button
                onClick={() => handleExtraction(true)}
                disabled={isLoading}
                className={`
                  w-full flex items-center justify-between p-4 border-2 rounded-lg transition-all
                  ${isLoading 
                    ? 'bg-gray-100 border-gray-200 cursor-not-allowed' 
                    : 'bg-white border-[#197ca8] text-[#197ca8] hover:bg-[#197ca8] hover:text-white'
                  }
                `}
              >
                <div className="flex items-center">
                  <FileText className="w-5 h-5 mr-3" />
                  <div className="text-left">
                    <div className="font-medium">Formulaire vierge</div>
                    <div className="text-xs opacity-70">À compléter manuellement</div>
                  </div>
                </div>
                {isLoading && <Loader2 className="w-4 h-4 animate-spin" />}
              </button>
            </div>

            {/* Bouton fermer */}
            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setShowModal(false)}
                disabled={isLoading}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ProfessionalPDFExtractionButton;